import PropTypes from 'prop-types';
import { useEffect, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// utils
import axios, { endpoints } from 'src/utils/axios';
//
import { AuthContext } from './auth-context';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  authenticated: false,
  loading: false,
  user: null,
};

// ----------------------------------------------------------------------


export function AuthProvider({ children }) {

  const navigate = useNavigate();

  const [authState, setAuthState] = useState(initialState);

  const initialize = useCallback(async () => {
    try {
      const response = await axios.get(endpoints.auth.check);
      const { authenticated, user } = response.data;
  
      if (authenticated) {
        setAuthState({ authenticated: true, user, loading: false });
        return response.data;
      }
    } catch (error) {
      navigate('/auth/jwt/login')
    }
  
    setAuthState(initialState);
    return initialState;
  }, []);


  const setUser = useCallback( (userData) => {

    setAuthState( {authenticated:false, user:{...userData}, loading:true} );

    // eslint-disable-next-line
  }, []);

  // LOGIN
  const login = useCallback(async (email, password) => {
    try {
      await axios.get(endpoints.auth.sanctum);  

      const response = await axios.post(endpoints.auth.login, { email, password });
      const { data } = response.data;

      setAuthState({ authenticated: true, user: data.user, loading: false });
      return data.user;
    } catch (error) {
      // console.error('Login failed:', error);
      throw error; // Permet à l'appelant de gérer l'erreur
    }
  }, []);

    // LOGIN
  const loginMagicLink = useCallback(async (email, token) => {
    try {
      await axios.get(endpoints.auth.sanctum);  

      const response = await axios.post(endpoints.auth.loginMagicLink, { email, token });
      const { data } = response.data;

      setAuthState({ authenticated: true, user: data.user, loading: false });
      return data.user;
    } catch (error) {
      // console.error('Login failed:', error);
      throw error; // Permet à l'appelant de gérer l'erreur
    }
  }, []);
   
  const sendMagicLink = useCallback(async (email, userID) => {
    try {

      const response = await axios.post(endpoints.auth.sendMagicLink, { email, userID });
      const { data } = response;

    } catch (error) {
      // console.error('Login failed:', error);
      throw error; // Permet à l'appelant de gérer l'erreur
    }
  }, []);

  const forgotPassword = useCallback(async (email) => {
    const formData = { email };
    const response = await axios.post(endpoints.auth.forgetPassword, formData);
    return response.data;
  }, []);

  const newPassword = useCallback(async (email, password, confirmPassword, token)  => {
    const formData = { email, password, password_confirmation:confirmPassword, token };

    const response = await axios.post(endpoints.auth.resetPassword, formData);

    return response.data;

  }, []);

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const data = {
      email,
      password,
      firstName,
      lastName,
    };

    const response = await axios.post(endpoints.auth.register, data);

    const { user } = response.data;

    setAuthState( {authenticated:true, user, loading:false} )

  }, []);

  // LOGOUT
  const logout = useCallback(async () => {

    const response = await axios.post(endpoints.auth.logout);

    setAuthState( {authenticated:false, user:null, loading:true} )

    navigate('/auth/jwt/login');
    // eslint-disable-next-line
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = authState.user ? 'authenticated' : 'unauthenticated';

  const status = authState.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: authState.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: authState.authenticated,
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      logout,
      setUser,
      initialize,
      forgotPassword,
      newPassword,
      loginMagicLink,
      sendMagicLink,
    }),
    [login, logout, setUser, register, forgotPassword, sendMagicLink, newPassword, loginMagicLink, initialize, authState.user, authState.authenticated, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
  
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};
